import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ShieldCheck, AlertCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useSettings, BrandLogo } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { fmtDateGB } from "../lib/format";

const ROLE_LABEL = {
  admin: "Administrator",
  manager: "Project Manager",
  epc: "EPC / Installer",
  contractor: "Contractor",
  investor: "Investor",
  customer: "Customer",
};

/**
 * Public invitation acceptance page (no auth required).
 * Recipient lands here from the invitation email, sets a password, and is
 * automatically signed in.
 */
export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/invitations/${token}`);
        setInfo(data);
      } catch (e) {
        setErr({
          status: e.response?.status,
          detail: formatApiError(e.response?.data?.detail, "This invitation could not be loaded."),
        });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post(`/public/invitations/${token}/accept`, { password });
      toast.success("Welcome aboard!");
      if (refresh) await refresh();
      nav("/", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const orgName = info?.org_name || settings?.org_name || "Flow";

  if (err) {
    return (
      <ErrorShell
        orgName={orgName}
        title={err.status === 410 ? "Invitation expired" : "Invitation not found"}
        message={err.detail}
      />
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="muted font-mono text-xs uppercase tracking-widest">Loading invitation…</div>
      </div>
    );
  }

  const inviter = info.invited_by?.name || info.invited_by?.email || `Your ${orgName} team`;
  const roleLabel = ROLE_LABEL[info.role] || info.role;

  return (
    <div className="min-h-screen bg-white" data-testid="invite-accept-page">
      <header
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #314041 0%, #3E5052 55%, #00B3BB 130%)",
          borderBottom: "4px solid #00B3BB",
        }}
      >
        <div className="max-w-3xl mx-auto px-8 py-6 flex items-center gap-3">
          <BrandLogo size={36} />
          <div>
            <div className="font-display font-bold text-xl leading-tight">{orgName}</div>
            <div
              className="uppercase tracking-[0.2em] text-[10px] mt-0.5"
              style={{ color: "rgba(255,255,255,0.72)" }}
            >
              Design · Quote · Install · Monitor
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-8 py-12">
        <div className="label" style={{ color: "var(--accent-hover)" }}>
          You've been invited
        </div>
        <h1
          className="font-display text-3xl md:text-4xl font-bold leading-tight mt-1"
          data-testid="invite-headline"
        >
          Hi {info.name?.split(" ")[0] || "there"}, set up your {orgName} account
        </h1>
        <p className="text-sm muted mt-3 max-w-xl">
          <strong>{inviter}</strong> has invited you to join <strong>{orgName}</strong> on Flow as a{" "}
          <span style={{ color: "var(--ink)" }}>{roleLabel}</span>. Pick a password below to activate
          your account — you'll be signed in straight after.
        </p>

        <div
          className="mt-6 p-3 rounded-md text-[12px] flex items-start gap-2"
          style={{ background: "var(--warm-soft)", borderLeft: "3px solid var(--warm)" }}
        >
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" style={{ color: "var(--warm-hover)" }} />
          <div>
            You're signing in as <span className="font-mono">{info.email}</span>.
            {info.expires_at && (
              <> · Invitation valid until {fmtDateGB(info.expires_at)}.</>
            )}
          </div>
        </div>

        <form
          onSubmit={submit}
          className="mt-8 max-w-md space-y-5"
          data-testid="invite-accept-form"
        >
          <label className="block">
            <span className="label block mb-1">Choose a password</span>
            <input
              type="password"
              required
              minLength={6}
              className="input-swiss"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              data-testid="invite-password-input"
              placeholder="Minimum 6 characters"
            />
          </label>
          <label className="block">
            <span className="label block mb-1">Confirm password</span>
            <input
              type="password"
              required
              minLength={6}
              className="input-swiss"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="invite-confirm-input"
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="btn-accent w-full justify-center"
            data-testid="invite-accept-btn"
          >
            <Check size={14} /> {busy ? "Activating…" : "Activate account & sign in"}
          </button>
        </form>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <FeatureCard
            icon={<ShieldCheck size={14} />}
            title="Secure by default"
            body="Your password is hashed with bcrypt. We never see or store it in plain text."
          />
          <FeatureCard
            icon={<Check size={14} />}
            title="Role-scoped access"
            body={`You'll only see the projects and tools relevant to your role (${roleLabel}).`}
          />
          <FeatureCard
            icon={<AlertCircle size={14} />}
            title="Wrong inbox?"
            body="If you weren't expecting this, you can safely ignore — the invite expires on its own."
          />
        </div>
      </main>
    </div>
  );
}

function ErrorShell({ orgName, title, message }) {
  return (
    <div className="min-h-screen bg-white">
      <header
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #314041 0%, #3E5052 55%, #00B3BB 130%)",
          borderBottom: "4px solid #00B3BB",
        }}
      >
        <div className="max-w-3xl mx-auto px-8 py-6 flex items-center gap-3">
          <BrandLogo size={36} />
          <div className="font-display font-bold text-xl">{orgName}</div>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-8 py-20 text-center">
        <AlertCircle size={40} className="mx-auto mb-4" style={{ color: "var(--warm)" }} />
        <h1 className="font-display text-3xl font-bold">{title}</h1>
        <p className="muted text-sm mt-3">{message}</p>
        <p className="muted text-xs mt-6">
          Ask your administrator to send a fresh invitation link.
        </p>
      </main>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--line)] p-4 rounded-md">
      <div className="flex items-center gap-2" style={{ color: "var(--accent-hover)" }}>
        {icon}
        <span className="label">{title}</span>
      </div>
      <div className="text-xs muted mt-1.5 leading-relaxed">{body}</div>
    </div>
  );
}
